/**
 * Genesis first-import wizard API (TMMAPP-0033 / TMMAPI-0059 + 0069).
 * Wire types from `src/api/generated/`; `payload` / `evidence` are free maps — typed
 * accessors return null fields for absent keys and never crash on extras.
 */
import { request } from './client';
import type {
  GenesisAccount as WireAccount,
  GenesisAcceptAllRequest,
  GenesisAcceptAllResponse as WireAcceptAll,
  GenesisAcceptRequest,
  GenesisAcceptResponse as WireAccept,
  GenesisPayeeGroup as WireGroup,
  GenesisProposalView as WireProposal,
  GenesisResponse as WireGenesis,
  GenesisRunResponse as WireRun,
  GenesisSuggested as WireSuggested,
  Recurrence as WireRecurrence,
} from './generated';
import { normalizeNamingStatus, type PayeeNamingStatus } from './payeeSuggestions';
import { normalizeCounterpartyReason, type CounterpartyReason } from './payees';
import { normalizeRecurrence, type Fulfilment, type RecurrenceRule } from './planned';

export type GenesisProposalKind = 'SERIES' | 'ENVELOPE';
export type GenesisProposalStatus = 'PROPOSED' | 'ACCEPTED' | 'DISMISSED' | 'SUPERSEDED';
export type GenesisSeriesKind = 'INCOME' | 'EXPENSE';

export interface GenesisAccount {
  id: number | null;
  name: string;
  historicalLineCount: number;
  firstLineOn: string | null;
}

export interface GenesisCoverage {
  namedPayeeShareBp: number;
  namedLineShareBp: number;
  /**
   * Every payee group after the exclusions (self-named counterparties, own-account transfers) and
   * the bank lines in them (TMMAPI-0174 §E): what the pages can show, never a page's size.
   */
  payeeGroupTotal: number;
  payeeLineTotal: number;
}

/**
 * The AI payee suggestion on a group. Its served `reason` is the model's own English text (TMM-B3):
 * it is not normalized, so no surface can render it; a shown suggestion carries the AiPill alone
 * (TMM-B9, TMMAPP-0122 item 21).
 */
export interface GenesisSuggested {
  suggestionId: number | null;
  proposedName: string;
  proposedPayeeId: number | null;
  confidence: number | null;
}

export interface GenesisGroup {
  payeeId: number | null;
  payeeNorm: string | null;
  displayName: string;
  lineCount: number;
  totalMinor: number | null;
  firstOn: string | null;
  lastOn: string | null;
  suggestedKind: string | null;
  suggestedAiExcluded: boolean;
  /** Why the classifier suggests keeping it private (TMMAPI-0157 D4); null for `NONE` or unknown. */
  suggestionReason: CounterpartyReason | null;
  suggested: GenesisSuggested | null;
  /** The group's newest raw bank line, as imported (TMMAPI-0174 §E, owner-only); null when none. */
  sampleText: string | null;
}

export interface GenesisProposal {
  id: number;
  kind: GenesisProposalKind;
  status: GenesisProposalStatus;
  score: number;
  payload: Record<string, unknown>;
  evidence: Record<string, unknown>;
  createdRef: number | null;
}

export interface GenesisRemaining {
  unnamedGroups: number;
  undecided: number;
}

export interface Genesis {
  budgetStartOn: string | null;
  accounts: GenesisAccount[];
  coverage: GenesisCoverage;
  namingStatus: PayeeNamingStatus;
  payeeGroups: GenesisGroup[];
  proposals: { series: GenesisProposal[]; envelopes: GenesisProposal[] };
  remaining: GenesisRemaining;
  /** Setup from history is turned off (TMMAPI-0176 §G): only resume turns it back on. */
  setupDismissed: boolean;
  /** When setup was turned off; null while it is on. */
  dismissedAt: string | null;
  nextCursor: number | null;
}

/** SERIES payload keys served today (TMMAPI-0059 GenesisAnalyzer). */
export interface SeriesPayload {
  identity: string | null;
  payeeId: number | null;
  payeeNorm: string | null;
  payee: string | null;
  accountId: number | null;
  kind: GenesisSeriesKind | null;
  amountMinor: number | null;
  recurrence: RecurrenceRule | null;
  fulfilment: Fulfilment | null;
  /** Served monthly-equivalent total when present — never invent one. */
  monthlyEquivalentMinor: number | null;
}

export interface SeriesEvidence {
  occurrenceCount: number | null;
  monthsObserved: number | null;
  payeeIds: number[];
  sampleTransactionIds: number[];
  medianAmountMinor: number | null;
  minAmountMinor: number | null;
  maxAmountMinor: number | null;
}

/**
 * ENVELOPE payload keys served today: identity, name, kind, targetMinor.
 * No additional keys are written by GenesisAnalyzer on this pin.
 */
export interface EnvelopePayload {
  identity: string | null;
  name: string | null;
  kind: string | null;
  targetMinor: number | null;
}

export interface EnvelopeEvidence {
  lineCount: number | null;
  monthsObserved: number | null;
  topPayees: string[];
  minMonthMinor: number | null;
  medianMonthMinor: number | null;
  maxMonthMinor: number | null;
}

export interface GenesisAcceptResult {
  proposalId: number | null;
  kind: GenesisProposalKind | null;
  createdRef: number | null;
}

export interface GenesisAcceptAllResult {
  accepted: number;
  proposalIds: number[];
  createdRefs: number[];
}

export interface GenesisRunResult {
  seriesProposed: number;
  envelopesProposed: number;
  superseded: number;
}

export type GenesisSeriesOverrides = {
  name?: string;
  payee?: string;
  amountMinor?: number;
  accountId?: number;
  startOn?: string;
  recurrence?: RecurrenceRule;
};

export type GenesisEnvelopeOverrides = {
  name?: string;
  targetMinor?: number;
  virtualAccountId?: number;
};

export type GenesisAcceptOverrides =
  | { series: GenesisSeriesOverrides }
  | { envelope: GenesisEnvelopeOverrides }
  | Record<string, never>;

/** The free `payload` / `evidence` maps: the typed accessors below re-check each key. */
type FreeMap = Record<string, unknown>;

const strOrNull = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const numOrNull = (v: unknown): number | null =>
  typeof v === 'number' && Number.isFinite(v) ? v : null;
const numList = (v: unknown): number[] =>
  Array.isArray(v)
    ? v.filter((id): id is number => typeof id === 'number' && Number.isFinite(id))
    : [];
const strList = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((s): s is string => typeof s === 'string') : [];

function normalizeAccount(w: WireAccount): GenesisAccount {
  return {
    id: w.id ?? null,
    name: w.name ?? '',
    historicalLineCount: w.historicalLineCount ?? 0,
    firstLineOn: w.firstLineOn ?? null,
  };
}

function normalizeSuggested(w: WireSuggested): GenesisSuggested {
  return {
    suggestionId: w.suggestionId ?? null,
    proposedName: w.proposedName ?? '',
    proposedPayeeId: w.proposedPayeeId ?? null,
    confidence: w.confidence ?? null,
  };
}

function normalizeGroup(w: WireGroup): GenesisGroup {
  return {
    payeeId: w.payeeId ?? null,
    payeeNorm: w.payeeNorm ?? null,
    displayName: w.displayName ?? '',
    lineCount: w.lineCount ?? 0,
    totalMinor: w.totalMinor ?? null,
    firstOn: w.firstOn ?? null,
    lastOn: w.lastOn ?? null,
    suggestedKind: w.suggestedKind ?? null,
    suggestedAiExcluded: w.suggestedAiExcluded ?? false,
    suggestionReason: normalizeCounterpartyReason(w.reasonCode, w.rail),
    suggested: w.suggested === undefined ? null : normalizeSuggested(w.suggested),
    sampleText: w.sampleText != null && w.sampleText.length > 0 ? w.sampleText : null,
  };
}

const PROPOSAL_STATUSES: ReadonlySet<string> = new Set([
  'PROPOSED',
  'ACCEPTED',
  'DISMISSED',
  'SUPERSEDED',
]);

/** Dropped without an id or with a kind this build doesn't know; an unknown status is PROPOSED. */
function normalizeProposal(w: WireProposal): GenesisProposal | null {
  if (w.id === undefined) return null;
  const kind = w.kind === 'ENVELOPE' ? 'ENVELOPE' : w.kind === 'SERIES' ? 'SERIES' : null;
  if (kind === null) return null;
  return {
    id: w.id,
    kind,
    status: w.status !== undefined && PROPOSAL_STATUSES.has(w.status) ? w.status : 'PROPOSED',
    score: w.score ?? 0,
    payload: w.payload ?? {},
    evidence: w.evidence ?? {},
    createdRef: w.createdRef ?? null,
  };
}

function normalizeProposals(rows: readonly WireProposal[] | undefined): GenesisProposal[] {
  return (rows ?? []).flatMap((row) => {
    const p = normalizeProposal(row);
    return p ? [p] : [];
  });
}

/** Every key of the genesis answer is optional on the wire; an absent one reads empty or zero. */
export function normalizeGenesis(w: WireGenesis): Genesis {
  return {
    budgetStartOn: w.budgetStartOn ?? null,
    accounts: (w.accounts ?? []).map(normalizeAccount),
    coverage: {
      namedPayeeShareBp: w.coverage?.namedPayeeShareBp ?? 0,
      namedLineShareBp: w.coverage?.namedLineShareBp ?? 0,
      payeeGroupTotal: w.coverage?.payeeGroupTotal ?? 0,
      payeeLineTotal: w.coverage?.payeeLineTotal ?? 0,
    },
    namingStatus: normalizeNamingStatus(w.namingStatus ?? {}),
    payeeGroups: (w.payeeGroups ?? []).map(normalizeGroup),
    proposals: {
      series: normalizeProposals(w.proposals?.series),
      envelopes: normalizeProposals(w.proposals?.envelopes),
    },
    remaining: {
      unnamedGroups: w.remaining?.unnamedGroups ?? 0,
      undecided: w.remaining?.undecided ?? 0,
    },
    setupDismissed: w.setupDismissed,
    dismissedAt: w.dismissedAt ?? null,
    nextCursor: w.nextCursor ?? null,
  };
}

/**
 * The payload's `recurrence` is a free-map value the analyzer writes in the `Recurrence` shape; it
 * is read as one when it carries a frequency and a start date.
 */
function recurrenceOf(v: unknown): RecurrenceRule | null {
  if (typeof v !== 'object' || v === null) return null;
  const r = v as FreeMap;
  if (typeof r.frequency !== 'string' || typeof r.startOn !== 'string') return null;
  return normalizeRecurrence(v as WireRecurrence);
}

export function seriesPayload(p: GenesisProposal): SeriesPayload {
  const w: FreeMap = p.payload;
  const kind = w.kind === 'INCOME' ? 'INCOME' : w.kind === 'EXPENSE' ? 'EXPENSE' : null;
  const fulfilment =
    w.fulfilment === 'AUTO_POST' ? 'AUTO_POST' : w.fulfilment === 'EXPECTED' ? 'EXPECTED' : null;
  return {
    identity: strOrNull(w.identity),
    payeeId: numOrNull(w.payeeId),
    payeeNorm: strOrNull(w.payeeNorm),
    payee: strOrNull(w.payee),
    accountId: numOrNull(w.accountId),
    kind,
    amountMinor: numOrNull(w.amountMinor),
    recurrence: recurrenceOf(w.recurrence),
    fulfilment,
    monthlyEquivalentMinor: numOrNull(w.monthlyEquivalentMinor),
  };
}

export function seriesEvidence(p: GenesisProposal): SeriesEvidence {
  const w: FreeMap = p.evidence;
  return {
    occurrenceCount: numOrNull(w.occurrenceCount),
    monthsObserved: numOrNull(w.monthsObserved),
    payeeIds: numList(w.payeeIds),
    sampleTransactionIds: numList(w.sampleTransactionIds),
    medianAmountMinor: numOrNull(w.medianAmountMinor),
    minAmountMinor: numOrNull(w.minAmountMinor),
    maxAmountMinor: numOrNull(w.maxAmountMinor),
  };
}

export function envelopePayload(p: GenesisProposal): EnvelopePayload {
  const w: FreeMap = p.payload;
  return {
    identity: strOrNull(w.identity),
    name: strOrNull(w.name),
    kind: strOrNull(w.kind),
    targetMinor: numOrNull(w.targetMinor),
  };
}

export function envelopeEvidence(p: GenesisProposal): EnvelopeEvidence {
  const w: FreeMap = p.evidence;
  return {
    lineCount: numOrNull(w.lineCount),
    monthsObserved: numOrNull(w.monthsObserved),
    topPayees: strList(w.topPayees),
    minMonthMinor: numOrNull(w.minMonthMinor),
    medianMonthMinor: numOrNull(w.medianMonthMinor),
    maxMonthMinor: numOrNull(w.maxMonthMinor),
  };
}

function wireRecurrence(rule: RecurrenceRule): WireRecurrence {
  return {
    frequency: rule.frequency,
    ...(rule.interval != null ? { interval: rule.interval } : {}),
    ...(rule.byWeekdays != null ? { byWeekdays: [...rule.byWeekdays] } : {}),
    ...(rule.byMonthDays != null ? { byMonthDays: [...rule.byMonthDays] } : {}),
    ...(rule.byWeekOrdinal != null ? { byWeekOrdinal: rule.byWeekOrdinal } : {}),
    ...(rule.byOrdinalWeekday != null ? { byOrdinalWeekday: rule.byOrdinalWeekday } : {}),
    ...(rule.byMonth != null ? { byMonth: rule.byMonth } : {}),
    startOn: rule.startOn,
    ...(rule.endsOn != null ? { endsOn: rule.endsOn } : {}),
    ...(rule.occurrenceCount != null ? { occurrenceCount: rule.occurrenceCount } : {}),
  };
}

function acceptBody(overrides: GenesisAcceptOverrides): GenesisAcceptRequest {
  if ('series' in overrides && overrides.series != null) {
    const s = overrides.series;
    return {
      series: {
        ...(s.name !== undefined ? { name: s.name } : {}),
        ...(s.payee !== undefined ? { payee: s.payee } : {}),
        ...(s.amountMinor !== undefined ? { amountMinor: s.amountMinor } : {}),
        ...(s.accountId !== undefined ? { accountId: s.accountId } : {}),
        ...(s.startOn !== undefined ? { startOn: s.startOn } : {}),
        ...(s.recurrence !== undefined ? { recurrence: wireRecurrence(s.recurrence) } : {}),
      },
    };
  }
  if ('envelope' in overrides && overrides.envelope != null) {
    const e = overrides.envelope;
    return {
      envelope: {
        ...(e.name !== undefined ? { name: e.name } : {}),
        ...(e.targetMinor !== undefined ? { targetMinor: e.targetMinor } : {}),
        ...(e.virtualAccountId !== undefined ? { virtualAccountId: e.virtualAccountId } : {}),
      },
    };
  }
  return {};
}

export const genesisApi = {
  /** GET /v1/me/genesis */
  async get(opts?: { limit?: number; cursor?: number }): Promise<Genesis> {
    const query: Record<string, number> = {};
    if (opts?.limit != null) query.limit = opts.limit;
    if (opts?.cursor != null) query.cursor = opts.cursor;
    return normalizeGenesis(
      await request<WireGenesis>('GET', '/v1/me/genesis', {
        ...(Object.keys(query).length > 0 ? { query } : {}),
      }),
    );
  },

  /** POST /v1/me/genesis/proposals/{id}/accept */
  async accept(id: number, overrides: GenesisAcceptOverrides = {}): Promise<GenesisAcceptResult> {
    const wire = await request<WireAccept>('POST', `/v1/me/genesis/proposals/${id}/accept`, {
      json: acceptBody(overrides),
    });
    return {
      proposalId: wire.proposalId ?? null,
      kind: wire.kind === 'SERIES' || wire.kind === 'ENVELOPE' ? wire.kind : null,
      createdRef: wire.createdRef ?? null,
    };
  },

  /** POST /v1/me/genesis/proposals/{id}/dismiss */
  async dismiss(id: number): Promise<void> {
    await request<void>('POST', `/v1/me/genesis/proposals/${id}/dismiss`);
  },

  /** POST /v1/me/genesis/proposals/accept-all */
  async acceptAll(kind: GenesisProposalKind, minScore?: number): Promise<GenesisAcceptAllResult> {
    const json: GenesisAcceptAllRequest = {
      kind,
      ...(minScore !== undefined ? { minScore } : {}),
    };
    const wire = await request<WireAcceptAll>('POST', '/v1/me/genesis/proposals/accept-all', {
      json,
    });
    return {
      accepted: wire.accepted ?? 0,
      proposalIds: wire.proposalIds ?? [],
      createdRefs: wire.createdRefs ?? [],
    };
  },

  /** POST /v1/me/genesis/run */
  async run(): Promise<GenesisRunResult> {
    const wire = await request<WireRun>('POST', '/v1/me/genesis/run');
    return {
      seriesProposed: wire.seriesProposed ?? 0,
      envelopesProposed: wire.envelopesProposed ?? 0,
      superseded: wire.superseded ?? 0,
    };
  },
};
